/**
 * A work's chain of title: the entries, their documents, and who has put a name to one.
 */
import type {
  AddProvenanceEntryRequest,
  AttachProvenancePrincipalRequest,
  AttestProvenanceRequest,
  JudgeProvenanceDocumentRequest,
  ProvenanceAttestationsResponse,
  ProvenanceCommentRequest,
  ProvenanceCommentsResponse,
  ProvenanceDocumentVerdictsResponse,
  ProvenanceDocumentsResponse,
  ProvenanceRequestsResponse,
  ProvenanceResponse,
  RequestVerificationRequest,
} from "@ondo/shared";

import { request } from "./request.ts";

/** A work's chain of title. */
export const fetchProvenance = (workId: string) =>
  request<ProvenanceResponse>(
    `/provenance?workId=${encodeURIComponent(workId)}`,
  ).then((body) => body.entries);

/** Returns the whole chain, because adding a link renumbers the ones after it. */
export const addProvenanceEntry = (body: AddProvenanceEntryRequest) =>
  request<ProvenanceResponse>("/provenance", body).then((response) => response.entries);

export const withdrawProvenanceEntry = (id: string) =>
  request<void>(`/provenance/${id}/withdraw`, {});

/**
 * Names an owner who is on ONDO.
 *
 * Takes an id the author already knew. There is no endpoint that turns a name
 * into one, deliberately: the server answers "no such id" and "not permitted"
 * with the same message so the id space cannot be walked, and a search here
 * would hand back what that refusal exists to withhold.
 */
export const attachProvenanceOndoId = (id: string, body: AttachProvenancePrincipalRequest) =>
  request<void>(`/provenance/${id}/ondo-id`, body);

export const fetchProvenanceDocuments = (id: string) =>
  request<ProvenanceDocumentsResponse>(`/provenance/${id}/documents`).then(
    (body) => body.documents,
  );

/**
 * Accepting or rejecting ONE paper — OND-62.
 *
 * The id is a DOCUMENT's, not an entry's, which is why this is a prefix of its
 * own on the server and why it is spelled out here rather than hung off
 * `/provenance/${entryId}/…`. An estate working through a dossier accepts three
 * and rejects the fourth, and the rejected one makes the entry read
 * questionable without anybody having disputed the claim.
 *
 * CALLING IT TWICE IS NOT AN ERROR. A second verdict from the same party
 * replaces the first — the server ends the live one in the same statement — so
 * a screen that lets somebody change their mind about a paper needs no withdraw
 * call in between.
 */
export const judgeProvenanceDocument = (id: string, body: JudgeProvenanceDocumentRequest) =>
  request<void>(`/provenance-documents/${id}/verdicts`, body);

/** Who accepted or rejected one paper, and for whom. */
export const fetchProvenanceDocumentVerdicts = (id: string) =>
  request<ProvenanceDocumentVerdictsResponse>(`/provenance-documents/${id}/verdicts`).then(
    (body) => body.verdicts,
  );

/** Taking a verdict back. The id is the VERDICT's — a party may have held several. */
export const withdrawProvenanceDocumentVerdict = (id: string) =>
  request<void>(`/provenance-document-verdicts/${id}/withdraw`, {});

export const fetchProvenanceAttestations = (id: string) =>
  request<ProvenanceAttestationsResponse>(`/provenance/${id}/attestations`).then(
    (body) => body.attestations,
  );

export const attestProvenance = (id: string, body: AttestProvenanceRequest) =>
  request<void>(`/provenance/${id}/attestations`, body);

export const fetchProvenanceComments = (id: string) =>
  request<ProvenanceCommentsResponse>(`/provenance/${id}/comments`).then(
    (body) => body.comments,
  );

export const commentOnProvenance = (id: string, body: ProvenanceCommentRequest) =>
  request<void>(`/provenance/${id}/comments`, body);

/** The asking side only — who was asked reads as doubt, and it is theirs. */
export const fetchProvenanceRequests = (id: string) =>
  request<ProvenanceRequestsResponse>(`/provenance/${id}/requests`).then(
    (body) => body.requests,
  );

export const requestProvenanceVerification = (id: string, body: RequestVerificationRequest) =>
  request<void>(`/provenance/${id}/requests`, body);

/**
 * Taking a position back.
 *
 * Called from the confirmations panel on a work's record, on the reader's own
 * live row and no other. It had no caller from 20260804090500 until then, which
 * is why `PROVENANCE_COPY.positionHeld` spent that time telling people to "take
 * the one you have back first" beside no control that could.
 *
 * The row is not deleted. `ended_at` is set and it stays on the list marked
 * withdrawn, because the position was held and a count somebody has already
 * seen must not silently disagree with itself.
 */
export const withdrawProvenanceAttestation = (id: string) =>
  request<void>(`/provenance-attestations/${id}/withdraw`, {});

/**
 * Taking a comment back — OND-21.
 *
 * Called from the same confirmations panel, on the reader's own live comment
 * and no other. `POST /v1/provenance-comments/:id/withdraw` had existed since
 * 20260804090500 with no caller, so a comment, once filed, was permanent in
 * practice while the record's own rule said it was not.
 *
 * Not a deletion, for the attestation's reason: `ended_at` is set and the row
 * stays on the list marked withdrawn. A comment is context rather than a
 * position and moves no count, so what the mark protects here is the thread —
 * a reply to a comment that had vanished would be answering nothing.
 */
export const withdrawProvenanceComment = (id: string) =>
  request<void>(`/provenance-comments/${id}/withdraw`, {});

/** Cancelling an ask. POST /provenance-requests/:id/withdraw exists; no screen calls it yet. */
export const withdrawProvenanceRequest = (id: string) =>
  request<void>(`/provenance-requests/${id}/withdraw`, {});
